import { Alert, AlertDescription, AlertIcon, AlertTitle, Box, CircularProgress, CircularProgressLabel, HStack, Link, List, ListIcon, ListItem, SimpleGrid, Stack, Stat, StatHelpText, StatLabel, StatNumber, Text } from '@chakra-ui/react';
import { Link as DefaultLink, useNavigate } from 'react-router-dom';
import React, { useEffect } from 'react';
import { customerFormTypes, deliveryType } from 'types/index';
import { fetchCompany, fetchCustomers, fetchDeliveries } from '../../store/actions';
import { getTodayTotal, getTotal } from '../../utils';
import { useAppDispatch, useAppSelector } from '../../hooks';

import { AppDispatch } from '../../store';
import { ChartView } from './ChartView';
import CountUp from 'react-countup';
import { CustomerTable } from './CustomerTable';
import { DeliveryTable } from './DeliveryTable';
import { HiOutlineArrowTrendingUp } from 'react-icons/hi2';
import { MdCheckCircle } from 'react-icons/md';
import moment from 'moment';

export const Dashboard = () => {

    const { user } = useAppSelector(state => state.userReducer);
    const { company } = useAppSelector(state => state.companyReducer);
    const { deliveries } = useAppSelector(state => state.deliveryReducer);
    const { customers } = useAppSelector(state => state.customersReducer);
    const { services } = useAppSelector(state => state.servicesReducer);
    const { vehicles } = useAppSelector(state => state.vehicleReducer);
    const dispatch: AppDispatch = useAppDispatch();
    const navigate = useNavigate();

    const { google_api_key, website } = company ?? { google_api_key: '' };

    const getSetupData = () => {
        const data = {
            google_api_key: {
                has_value: (google_api_key !== '' && google_api_key !== undefined && google_api_key !== null) ? true : false,
                value: (google_api_key !== '' && google_api_key !== undefined && google_api_key !== null) ? 25 : 0,
            },
            services: {
                has_value: services?.length !== 0 ? true : false,
                value: services?.length !== 0 ? 25 : 0,
            },
            vehicles: {
                has_value: vehicles.length !== 0 ? true : false,
                values: vehicles.length ? 25 : 0,
            },
            terms_and_conditions: {
                has_value: website?.terms_and_conditions?.length !== 0 ? true : false,
                value: website?.terms_and_conditions?.length !== 0 ? 25 : 0,
            }
        };
        const total = data?.google_api_key?.value + data?.services?.value + data?.vehicles?.values + data?.terms_and_conditions?.value;

        return {
            total,
            ...data
        };
    };

    const getYesterdayData = () => {
        const yesterday_date = moment().day(-1).format('YYYY-MM-DD');
        const yesterday_deliveries: deliveryType [] = deliveries.filter((delivery: deliveryType) => moment(delivery.date).format('YYYY-MM-DD') === yesterday_date && delivery.status === 'delivered');
        const yesterday_customers: customerFormTypes [] = customers.filter((customer: customerFormTypes) => moment(customer.createdAt).format('YYYY-MM-DD') === yesterday_date);
        return { 
            total: Number(getTotal(yesterday_deliveries)),
            deliveryLength: yesterday_deliveries.length,
            customerLength: yesterday_customers.length
        };
    };

    useEffect(() => {
        if (user?.company_id) {
            // dispatch(getUser()).then(() => dispatch(fetchCompany()));
            dispatch(fetchCompany());
            dispatch(fetchDeliveries());
            dispatch(fetchCustomers());
        } else {
            navigate('/company/add');
        }


        // return () => {
        //     dispatch(fetchCompany());
        // };
    }, []);

    return (
        <Stack>
            {
                getSetupData().total !== 100 &&
                <Stack bg={'#ffffff'} p={3} borderRadius={6} mb={2}>
                    <SimpleGrid columns={[1, 2, 2, 2, 2]} spacing={4}>
                        <Box>
                            <SimpleGrid columns={[1, 1, 2, 2, 2]}>
                                <Box alignItems={'center'} justifyContent={'center'} display={'d-flex'}>
                                    <CircularProgress value={getSetupData().total} trackColor={'#c8e6c9'} color='green.500' size='150px'>
                                        <CircularProgressLabel>{getSetupData().total}%</CircularProgressLabel>
                                    </CircularProgress>
                                </Box>
                                <Box>
                                    <Text fontSize={'2xl'} fontWeight={'semibold'}>Quick Start</Text>
                                    <Text color={'gray.500'}>Walk through this guide to get the most out of Trucklivery Management System right away.</Text>
                                </Box>
                            </SimpleGrid>
                        </Box>
                        <Box p={3} borderRadius={4} bg={'#fff8e1'}>
                            <Text fontSize={'lg'} fontWeight={'semibold'} mb={1}>Next steps:</Text>
                            <List spacing={1}>
                                <ListItem>
                                    <ListIcon as={MdCheckCircle} color={getSetupData().google_api_key?.has_value ? 'green.500' : 'gray.500'} />
                                    <Link textDecoration={'underline'} as={DefaultLink} to={'/developer'}>Add Google API Key</Link>
                                </ListItem>
                                <ListItem>
                                    <ListIcon as={MdCheckCircle} color={getSetupData().services?.has_value ? 'green.500' : 'gray.500'} />
                                    <Link textDecoration={'underline'} as={DefaultLink} to={'/services'}>Add services.</Link>
                                </ListItem>
                                <ListItem>
                                    <ListIcon as={MdCheckCircle} color={getSetupData().vehicles?.has_value ? 'green.500' : 'gray.500'} />
                                    <Link textDecoration={'underline'} as={DefaultLink} to={'/vehicles'}>Add Fleets and vehicles.</Link>
                                </ListItem>
                                <ListItem>
                                    <ListIcon as={MdCheckCircle} color={getSetupData().terms_and_conditions?.has_value ? 'green.500' : 'gray.500'} />
                                    <Link textDecoration={'underline'} as={DefaultLink} to={'/settings'}>Add Business terms and conditions.</Link>
                                </ListItem>
                            </List>
                        </Box>
                    </SimpleGrid>
                </Stack>
            }
            <Alert status='success' borderRadius={6} mb={3}>
                <AlertIcon />
                <Box>
                    <AlertTitle>Good news, more money!</AlertTitle>
                    <AlertDescription>
                        We don&apos;t take 15% of each delivery you make, we&apos;ll be giving you certain percentage of adverts revenue 😌. You&apos;ll still be making money without making deliveries.
                    </AlertDescription>
                </Box>
            </Alert>
            <SimpleGrid columns={[1, 2, 2, 3]} spacing={4}>
                <Stat bgColor={'white'} px={4} py={3} borderRadius={6}>
                    <StatLabel textTransform={'uppercase'} color={'gray.500'} fontSize={'xs'} fontWeight={'medium'}>Today delivered total</StatLabel>
                    <HStack>
                        <CountUp start={0} end={getTodayTotal(deliveries).toFixed(2) || 0} decimals={2} duration={2}>
                            {
                                ({ countUpRef }) => (
                                    <StatNumber fontSize={'4xl'}>R <span ref={countUpRef}>{getTodayTotal(deliveries).toFixed(2) || 0}</span></StatNumber>
                                )
                            }
                        </CountUp>
                        <HiOutlineArrowTrendingUp color='#4caf50' size={24} fontWeight={700} />
                        <Text fontSize={'sm'} fontWeight={'semibold'} color={'green.500'}>+R{getYesterdayData().total.toFixed(2)}</Text>
                    </HStack>
                    <StatHelpText color={'gray.500'} fontSize={'xs'}>Total amount accumulated today</StatHelpText>
                </Stat>
                <Stat bgColor={'white'} px={4} py={3} borderRadius={6}>
                    <StatLabel textTransform={'uppercase'} color={'gray.500'} fontSize={'xs'} fontWeight={'medium'}>Deliveries</StatLabel>
                    <HStack>
                        <CountUp start={0} end={deliveries.length || 0} duration={2}>
                            {
                                ({ countUpRef }) => (
                                    <StatNumber fontSize={'4xl'} ref={countUpRef}>{deliveries.length || 0}</StatNumber>
                                )
                            }
                        </CountUp>
                        <HiOutlineArrowTrendingUp color='#4caf50' size={24} fontWeight={700} />
                        <Text fontSize={'sm'} fontWeight={'semibold'} color={'green.500'}>+{getYesterdayData().deliveryLength}</Text>
                    </HStack>
                    <StatHelpText color={'gray.500'} fontSize={'xs'}>All deliveries</StatHelpText>
                </Stat>
                <Stat bgColor={'white'} px={4} py={3} borderRadius={6}>
                    <StatLabel textTransform={'uppercase'} color={'gray.500'} fontSize={'xs'} fontWeight={'medium'}>Customers</StatLabel>
                    <HStack>
                        <CountUp start={0} end={customers.length || 0} duration={2}>
                            {
                                ({ countUpRef }) => (
                                    <StatNumber fontSize={'4xl'} ref={countUpRef}>{customers.length || 0}</StatNumber>
                                )
                            }
                        </CountUp>
                        <HiOutlineArrowTrendingUp color='#4caf50' size={24} fontWeight={700} />
                        <Text fontSize={'sm'} fontWeight={'semibold'} color={'green.500'}>+{getYesterdayData().customerLength}</Text>
                    </HStack>
                    <StatHelpText color={'gray.500'} fontSize={'xs'}>All customers</StatHelpText>
                </Stat>
            </SimpleGrid>
            <SimpleGrid columns={1} spacing={4}>
                <ChartView deliveries={deliveries} />
            </SimpleGrid>
            <HStack>
                <SimpleGrid columns={[1, 1, 1, 2]} spacing={4}>
                    <DeliveryTable deliveries={deliveries} />
                    <CustomerTable customers={customers} />
                </SimpleGrid>
            </HStack>
        </Stack>
    );
};